import { useCallback, useRef, useState } from 'react';
import { Carga, mensagemDoErroDeCarga, ResultadoDoLote } from './cargaDaLista';

/**
 * O estado de uma carga de lista: carregando, erro (com o que aconteceu) ou pronto. Ver design/cargaDaLista.
 *
 * ⛔ O hook NÃO guarda nem toca nos dados da tela, de propósito. Quem carrega só grava a lista quando a chamada
 *    dá certo; quando falha, a lista que já estava na tela fica. Erro não zera os dados: recarga que falha e apaga
 *    a lista que estava funcionando transforma uma falha temporária em perda visível.
 *
 * Uso: `const carga = useEstadoDaCarga();` e, na função que carrega,
 *      `await carga.rodar(async () => { setLista(await api.listar()); });`
 * O `rodar` não lança: o erro vira estado, para a tela mostrar.
 *
 * Carga de LOTE (design/cargaDaLista → lerEmLote): devolva o `resultado` no fim do `carregar`. Se faltou algo, o
 * estado é 'parcial', com o que faltou nomeado; se faltou TUDO, é 'erro'.
 */
export function useEstadoDaCarga() {
  const [carga, setCarga] = useState<Carga>({ estado: 'carregando', erro: null, atualizadoEm: null });
  // Só a carga mais recente decide o estado: uma resposta velha que chega depois não sobrescreve a nova.
  const ultima = useRef(0);

  const rodar = useCallback(async (carregar: () => Promise<void | ResultadoDoLote>) => {
    const minha = ++ultima.current;
    setCarga((c) => (c.estado === 'erro' ? c : { ...c, estado: c.atualizadoEm ? c.estado : 'carregando' }));
    try {
      const lote = await carregar();
      if (minha !== ultima.current) return;
      if (lote && lote.faltaram.length > 0) {
        if (lote.faltaram.length >= lote.total) {
          setCarga((c) => ({ ...c, estado: 'erro', erro: lote.erro, faltaram: lote.faltaram }));
        } else {
          setCarga({ estado: 'parcial', erro: lote.erro, atualizadoEm: new Date().toISOString(), faltaram: lote.faltaram });
        }
        return;
      }
      setCarga({ estado: 'pronto', erro: null, atualizadoEm: new Date().toISOString() });
    } catch (e) {
      if (minha !== ultima.current) return;
      setCarga((c) => ({ ...c, estado: 'erro', erro: mensagemDoErroDeCarga(e) }));
    }
  }, []);

  return { ...carga, rodar };
}
